import type { AnyRoute } from '@tanstack/react-router';
import {
  createRoute,
  lazyRouteComponent,
  redirect
} from '@tanstack/react-router';
import { z } from 'zod';
import { verifySession } from '#domains/auth';
import { useAppStore } from '#shared/hooks';
import { loadScript as loadReCaptchaScript } from '#shared/modules/google-recaptcha';
import { useAuthStore } from './hooks';
import AuthLayout from './layouts/AuthLayout';

const authSearchSchema = z.object({
  error: z.string().optional()
});

export const createAuthRoutes = <TParent extends AnyRoute>(parent: TParent) => {
  const authRoute = createRoute({
    id: 'auth',
    getParentRoute: () => parent,
    component: AuthLayout,
    validateSearch: authSearchSchema,
    beforeLoad: async ({ location, search }) => {
      if (
        search.error === 'token_expired' ||
        search.error === 'invalid_token'
      ) {
        throw redirect({ to: '/verification-failed' });
      }

      if (
        location.pathname !== '/sign-in' &&
        location.pathname !== '/sign-up'
      ) {
        return;
      }

      const result = await verifySession();

      if (result?.data) {
        throw redirect({ to: '/', replace: true });
      }
    },
    onEnter: () => {
      if (!useAppStore.getState().isExt) {
        loadReCaptchaScript();
      }
    }
  });

  const signInRoute = createRoute({
    path: '/sign-in',
    getParentRoute: () => authRoute,
    component: lazyRouteComponent(() => import('./pages/SignInPage'))
  });

  const signUpRoute = createRoute({
    path: '/sign-up',
    getParentRoute: () => authRoute,
    component: lazyRouteComponent(() => import('./pages/SignUpPage'))
  });

  const emailSentRoute = createRoute({
    path: '/email-sent',
    getParentRoute: () => authRoute,
    component: lazyRouteComponent(() => import('./pages/EmailSentPage')),
    beforeLoad: () => {
      const email = useAuthStore.getState().email;

      if (!email) {
        throw redirect({ to: '/sign-in' });
      }
    },
    onLeave: () => {
      useAuthStore.getState().clearEmail();
    }
  });

  const forgotPasswordRoute = createRoute({
    path: '/forgot-password',
    getParentRoute: () => authRoute,
    component: lazyRouteComponent(() => import('./pages/ForgotPasswordPage'))
  });

  const resetPasswordRoute = createRoute({
    path: '/reset-password',
    getParentRoute: () => authRoute,
    component: lazyRouteComponent(() => import('./pages/ResetPasswordPage')),
    beforeLoad: ({ search }) => {
      if (!search.token) {
        throw redirect({ to: '/sign-in' });
      }
    }
  });

  const verificationFailedRoute = createRoute({
    path: '/verification-failed',
    getParentRoute: () => authRoute,
    component: lazyRouteComponent(
      () => import('./pages/VerificationFailedPage')
    )
  });

  return [
    authRoute.addChildren([
      signInRoute,
      signUpRoute,
      emailSentRoute,
      forgotPasswordRoute,
      resetPasswordRoute,
      verificationFailedRoute
    ])
  ];
};
